import { useState, useRef, useEffect } from 'react';

export interface FAQItem {
  q: string;
  a: string;
}

interface Props {
  title?: string;
  faqs: FAQItem[];
}

export default function FAQSection({ title = 'Frequently Asked Questions', faqs }: Props) {
  const [open, setOpen] = useState<number | null>(0);
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 lg:py-32" style={{ background: '#FFFFFF' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-3 gap-16">
          {/* Left heading (sticky) */}
          <div
            className="lg:col-span-1"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateX(0)' : 'translateX(-24px)',
              transition: 'opacity 0.7s ease, transform 0.7s ease',
            }}
          >
            <div className="lg:sticky lg:top-32">
              <span className="section-tag mb-4 block">Voice Search Optimized</span>
              <h2 className="font-serif text-4xl lg:text-5xl text-charcoal leading-tight mb-6">
                {title}
              </h2>
              <div className="gold-line mb-8" />
              <p className="text-warm-gray text-sm leading-relaxed mb-8">
                Answers structured for conversational queries — ask Siri, Alexa, Google, or ChatGPT about flooring in New Jersey.
              </p>
              <div className="flex items-center gap-3 p-4 bg-cream rounded-lg border border-cream-dark">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-charcoal flex-shrink-0">
                  <i className="ri-mic-line text-gold text-sm"></i>
                </span>
                <p className="text-charcoal text-xs font-medium leading-snug">
                  "Hey Siri, who installs carpet near Freehold NJ?"
                </p>
              </div>
            </div>
          </div>

          {/* Right FAQs */}
          <div
            className="lg:col-span-2 space-y-3"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateX(0)' : 'translateX(24px)',
              transition: 'opacity 0.7s ease 0.15s, transform 0.7s ease 0.15s',
            }}
          >
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`border rounded-lg overflow-hidden transition-colors duration-200 ${
                  open === i ? 'border-gold/40 bg-cream' : 'border-cream-dark bg-white hover:border-charcoal/20'
                }`}
              >
                <button
                  className="w-full text-left px-7 py-6 flex items-start justify-between gap-6 cursor-pointer"
                  onClick={() => setOpen(open === i ? null : i)}
                >
                  <span className={`font-serif text-base leading-snug transition-colors ${open === i ? 'text-charcoal' : 'text-charcoal/80'}`}>
                    {faq.q}
                  </span>
                  <span
                    className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 transition-transform duration-200 text-gold"
                    style={{ transform: open === i ? 'rotate(45deg)' : 'none' }}
                  >
                    <i className="ri-add-line text-base"></i>
                  </span>
                </button>
                <div
                  className="overflow-hidden transition-all duration-300"
                  style={{ maxHeight: open === i ? '400px' : '0' }}
                >
                  <p className="px-7 pb-6 text-warm-gray text-sm leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
